import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { Field } from "@/admin/Field";

export default function CrudManager({ schema }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // object or null
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiCat, setAiCat] = useState("Mindset");
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => api.get(schema.endpoint, { params: schema.listQuery }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [schema.endpoint]);

  const openNew = () => {
    const blank = {};
    schema.fields.forEach((f) => { if (f.default !== undefined) blank[f.key] = f.default; });
    setEditing(blank);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      if (payload.id) {
        await api.put(`${schema.endpoint}/${payload.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(schema.endpoint, payload);
        toast.success("Created");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`${schema.endpoint}/${id}`);
    toast.success("Deleted");
    load();
  };

  const runAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/draft", { topic: aiTopic, category: aiCat });
      setEditing({
        title: data.title || aiTopic,
        subtitle: data.subtitle || "",
        excerpt: data.excerpt || "",
        body: data.body_html || "",
        category: aiCat,
        reflection_question: data.reflection_question || "",
        practical_application: data.practical_application || "",
        author: "Lance Cervantes",
        reading_time: 5,
        status: "draft",
      });
      setAiOpen(false);
      toast.success("Draft generated — review and edit before publishing.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setAiLoading(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif-display text-2xl font-bold text-[#111827]">{schema.label}</h1>
        <div className="flex gap-2">
          {schema.ai && (
            <button data-testid="admin-ai-draft" onClick={() => setAiOpen(true)} className="inline-flex items-center gap-2 border border-[#0F2C59] text-[#0F2C59] px-4 py-2 text-sm font-semibold hover:bg-slate-50">
              <Sparkles size={16} /> AI Draft
            </button>
          )}
          <button data-testid="admin-new-item" onClick={openNew} className="inline-flex items-center gap-2 bg-[#0F2C59] text-white px-4 py-2 text-sm font-semibold hover:bg-[#174082]">
            <Plus size={16} /> New
          </button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {schema.columns.map((c) => <th key={c} className="text-left px-4 py-3 font-semibold text-slate-600 capitalize">{c.replace("_", " ")}</th>)}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={schema.columns.length + 1} className="px-4 py-8 text-center text-slate-400">No items yet.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} data-testid={`admin-row-${it.id}`} className="hover:bg-slate-50">
                {schema.columns.map((c) => (
                  <td key={c} className="px-4 py-3 text-slate-700">
                    {typeof it[c] === "boolean" ? (it[c] ? "Yes" : "—") : String(it[c] ?? "—").slice(0, 60)}
                  </td>
                ))}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`admin-edit-${it.id}`} onClick={() => setEditing(it)} className="text-slate-500 hover:text-[#0F2C59] p-1"><Pencil size={16} /></button>
                  <button data-testid={`admin-delete-${it.id}`} onClick={() => remove(it.id)} className="text-slate-500 hover:text-red-500 p-1 ml-1"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI modal */}
      {aiOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAiOpen(false)}>
          <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif-display text-xl font-bold mb-4">AI Draft Assistant</h2>
            <label className="text-xs font-semibold text-slate-500">Topic</label>
            <input data-testid="ai-topic" className="w-full border border-slate-300 px-3 py-2 mt-1 mb-3 text-sm" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g. How self-talk shapes discipline" />
            <label className="text-xs font-semibold text-slate-500">Category</label>
            <select className="w-full border border-slate-300 px-3 py-2 mt-1 bg-white text-sm" value={aiCat} onChange={(e) => setAiCat(e.target.value)}>
              {["Mindset", "Discipline", "Resilience", "Leadership", "Relationships", "Intentional Living"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <button data-testid="ai-generate" disabled={aiLoading || !aiTopic} onClick={runAI} className="w-full bg-[#0F2C59] text-white py-2.5 mt-5 text-sm font-semibold disabled:opacity-50">
              {aiLoading ? "Generating…" : "Generate Draft"}
            </button>
          </div>
        </div>
      )}

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto im-scrollbar" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-serif-display text-xl font-bold">{editing.id ? "Edit" : "New"} {schema.label.replace(/s$/, "")}</h2>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-4">
              {schema.fields.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                  <Field field={f} value={editing[f.key]} onChange={(val) => setEditing({ ...editing, [f.key]: val })} />
                  {f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>}
                </div>
              ))}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3">
              <button data-testid="admin-save" onClick={save} disabled={saving} className="bg-[#0F2C59] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#174082] disabled:opacity-50">
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="border border-slate-300 px-6 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
